import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEBINAR_AUDIENCE_LABEL, WEBINAR_LAYOUT_LABEL, WEBINAR_LAYOUTS, WEBINAR_AUDIENCES } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtCountdown, fmtDate, fmtTime, useNow } from '../lib/format';
import { downloadIcs, memberLiveApi, utcToZoned, WEBINAR_STATUS_LABEL, WEBINAR_STATUS_TONE, zonedToUtc, type WebinarSummary, type WebinarView } from '../lib/live';
import type { LibraryItem } from '../lib/library';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';
import { WebinarRoom } from '../components/WebinarRoom';
import { BroadcastPanel } from '../components/Broadcast';
import { RecordingsCard } from '../components/Recordings';

interface ListData {
  livekit: { configured: boolean };
  canSchedule: boolean;
  webinars: WebinarSummary[];
}

export const StatusPill = ({ s }: { s: WebinarSummary['status'] }) => <Pill tone={WEBINAR_STATUS_TONE[s]}>{WEBINAR_STATUS_LABEL[s]}</Pill>;

function WebinarRow({ w, tz }: { w: WebinarSummary; tz: string }) {
  return (
    <Link to={`/webinars/${w.id}`} className="block text-ink no-underline hover:text-ink">
      <Card tone={w.status === 'LIVE' ? 'up' : undefined} className="flex flex-col gap-1.5 p-4 transition-colors hover:border-line2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="text-base font-semibold">{w.title}</span>
          <span className="flex gap-1.5">
            {w.registered && <Pill tone="accent">Registered</Pill>}
            <StatusPill s={w.status} />
          </span>
        </div>
        <span className="text-sm text-sub">
          {fmtTime(w.startsAt, tz, true)} · {w.durationMinutes} min · {w.host.name}
        </span>
        <span className="text-xs text-muted">{w.audienceLabel}</span>
      </Card>
    </Link>
  );
}

/** A month grid: days with webinars carry a dot; tapping a day lists them below (the grid stays 7 narrow columns at 390 px). */
function MonthCalendar({ webinars, tz, month, setMonth, day, setDay }: { webinars: WebinarSummary[]; tz: string; month: Date; setMonth: (d: Date) => void; day: string | null; setDay: (d: string | null) => void }) {
  const key = (d: Date | string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d));
  const byDay = useMemo(() => {
    const m = new Map<string, WebinarSummary[]>();
    for (const w of webinars) m.set(key(w.startsAt), [...(m.get(key(w.startsAt)) ?? []), w]);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinars, tz]);
  const y = month.getUTCFullYear();
  const mo = month.getUTCMonth();
  const first = new Date(Date.UTC(y, mo, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday first
  const days = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const todayKey = key(new Date());
  return (
    <Card className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <Button variant="ghost" aria-label="Previous month" onClick={() => setMonth(new Date(Date.UTC(y, mo - 1, 1)))}>‹</Button>
        <strong>{new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(first)}</strong>
        <Button variant="ghost" aria-label="Next month" onClick={() => setMonth(new Date(Date.UTC(y, mo + 1, 1)))}>›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => <span key={d}>{d}</span>)}
        {cells.map((c, i) => {
          if (c === null) return <span key={`b${i}`} />;
          const k = `${y}-${String(mo + 1).padStart(2, '0')}-${String(c).padStart(2, '0')}`;
          const list = byDay.get(k);
          return (
            <button key={k} type="button" onClick={() => setDay(day === k ? null : k)} aria-label={`${k}${list ? `, ${list.length} webinar${list.length > 1 ? 's' : ''}` : ''}`} aria-pressed={day === k} className={cx('flex min-h-11 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg border text-sm', day === k ? 'border-accent bg-accent-bg text-ink' : k === todayKey ? 'border-line2 bg-raised text-ink' : 'border-transparent bg-transparent text-ink2')}>
              {c}
              <span className={cx('h-1.5 w-1.5 rounded-full', list ? (list.some((w) => w.status === 'LIVE') ? 'bg-up' : 'bg-accent') : 'bg-transparent')} />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export function WebinarsList() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [view, setView] = useState<'list' | 'month'>('list');
  const [month, setMonth] = useState(() => new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)));
  const [day, setDay] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['webinars'], queryFn: () => api<ListData>('/webinars'), refetchInterval: 30_000 });
  const all = data?.webinars ?? [];
  const upcoming = all.filter((w) => w.status === 'LIVE' || w.status === 'SCHEDULED');
  const past = all.filter((w) => w.status === 'ENDED' || w.status === 'CANCELLED').reverse();
  const inDay = day ? all.filter((w) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(w.startsAt)) === day) : [];
  return (
    <>
      <PageHeader
        title="Webinars"
        sub="Live sessions with the Master Traders · RSVP for reminders"
        right={data?.canSchedule && <Link to="/webinars/new" className="rounded-lg bg-accent px-4 py-2.5 font-bold text-bg no-underline hover:text-bg">+ Schedule a webinar</Link>}
      />
      {data && !data.livekit.configured && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">Live video isn’t set up yet. You can schedule, RSVP and send questions; the stage opens once LiveKit is connected.</p>}
      <Segmented label="View" options={[{ value: 'list', label: 'List' }, { value: 'month', label: 'Month' }]} value={view} onChange={setView} />
      {view === 'month' && (
        <>
          <MonthCalendar webinars={all} tz={tz} month={month} setMonth={setMonth} day={day} setDay={setDay} />
          {day && (
            <section aria-label={`Webinars on ${day}`} className="flex flex-col gap-2">
              {!inDay.length && <Card><Empty>No webinars on this day.</Empty></Card>}
              {inDay.map((w) => <WebinarRow key={w.id} w={w} tz={tz} />)}
            </section>
          )}
        </>
      )}
      {view === 'list' && (
        <>
          <section aria-label="Upcoming webinars" className="flex flex-col gap-2.5">
            {isLoading && <Card className="p-4 text-sub">Loading…</Card>}
            {!isLoading && !upcoming.length && <Card><Empty>No webinars are scheduled.</Empty></Card>}
            {upcoming.map((w) => <WebinarRow key={w.id} w={w} tz={tz} />)}
          </section>
          {!!past.length && (
            <section aria-label="Past webinars" className="flex flex-col gap-2.5">
              <h2 className="m-0 text-sm font-semibold text-sub">Past</h2>
              {past.slice(0, 20).map((w) => <WebinarRow key={w.id} w={w} tz={tz} />)}
            </section>
          )}
        </>
      )}
    </>
  );
}

// ───────────────────────────── schedule / edit ─────────────────────────────

interface Person { id: string; name: string }

export function WebinarForm() {
  const { id } = useParams();
  const editing = !!id;
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const nav = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ['webinar', id], queryFn: () => api<WebinarView>(`/webinars/${id}`), enabled: editing });
  const { data: markets } = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string }[]>('/markets'), staleTime: 300_000 });
  const { data: people } = useQuery({ queryKey: ['live-people'], queryFn: () => api<Person[]>('/live/people'), staleTime: 300_000 });
  const { data: library } = useQuery({ queryKey: ['library', 'for-webinar'], queryFn: () => api<LibraryItem[]>('/library'), staleTime: 300_000 });
  const w = existing.data;
  const [f, setF] = useState<{ title: string; description: string; startsAt: string; durationMinutes: number; audienceMode: 'FLOOR' | 'MARKETS' | 'SELECTED' | 'GUESTS'; marketIds: string[]; userIds: string[]; coHostIds: string[]; libraryItemIds: string[] } | null>(null);
  const form = f ?? {
    title: w?.title ?? '',
    description: w?.description ?? '',
    startsAt: w ? utcToZoned(w.startsAt, tz) : '',
    durationMinutes: w?.durationMinutes ?? 60,
    audienceMode: w?.audienceMode ?? ('FLOOR' as const),
    marketIds: w?.markets.map((m) => m.id) ?? [],
    userIds: w?.selected.map((p) => p.id) ?? [],
    coHostIds: w?.coHosts.map((p) => p.id) ?? [],
    libraryItemIds: w?.attachments.map((a) => a.id) ?? [],
  };
  const set = (patch: Partial<typeof form>) => setF({ ...form, ...patch });
  const toggle = (k: 'marketIds' | 'userIds' | 'coHostIds' | 'libraryItemIds', v: string, on: boolean) => set({ [k]: on ? [...form[k], v] : form[k].filter((x) => x !== v) });
  const save = useMutation({
    mutationFn: () => {
      const startsAt = zonedToUtc(form.startsAt, tz);
      const body = { ...form, startsAt: startsAt.toISOString(), description: form.description.trim() || null, durationMinutes: Number(form.durationMinutes) };
      return editing ? api<WebinarView>(`/webinars/${id}`, { method: 'PATCH', body }) : api<WebinarView>('/webinars', { body });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['webinars'] });
      void qc.invalidateQueries({ queryKey: ['webinar', r.id] });
      nav(`/webinars/${r.id}`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const checks = (title: string, k: 'marketIds' | 'userIds' | 'coHostIds' | 'libraryItemIds', rows: { id: string; label: string }[] | undefined) => (
    <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
      <legend className="mb-1 text-xs text-muted">{title}</legend>
      <div className="flex max-h-40 flex-col overflow-y-auto">
        {rows?.map((r) => (
          <label key={r.id} className="flex min-h-9 items-center gap-2 text-sm">
            <input type="checkbox" checked={form[k].includes(r.id)} onChange={(e) => toggle(k, r.id, e.target.checked)} />
            {r.label}
          </label>
        ))}
        {!rows?.length && <span className="text-sm text-muted">None available.</span>}
      </div>
    </fieldset>
  );
  return (
    <>
      <PageHeader back={<Link to={editing ? `/webinars/${id}` : '/webinars'}>← {editing ? 'Webinar' : 'Webinars'}</Link>} title={editing ? 'Edit webinar' : 'Schedule a webinar'} sub={`Times are in your timezone (${tz})`} />
      <form onSubmit={submit} className="flex max-w-2xl flex-col gap-4">
        <Card className="flex flex-col gap-3 p-4">
          <Field label="Title"><Input value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={160} required /></Field>
          <Field label="Agenda"><Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={4} maxLength={5000} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Starts"><Input type="datetime-local" value={form.startsAt} onChange={(e) => set({ startsAt: e.target.value })} required /></Field>
            <Field label="Length (minutes)"><Input type="number" min={15} max={480} step={5} value={form.durationMinutes} onChange={(e) => set({ durationMinutes: Number(e.target.value) })} required /></Field>
          </div>
        </Card>
        <Card className="flex flex-col gap-3 p-4">
          <Field label="Who can see it">
            <Select value={form.audienceMode} onChange={(e) => set({ audienceMode: e.target.value as typeof form.audienceMode })}>
              {WEBINAR_AUDIENCES.map((a) => <option key={a} value={a}>{WEBINAR_AUDIENCE_LABEL[a]}</option>)}
            </Select>
          </Field>
          {form.audienceMode === 'MARKETS' && checks('Market desks', 'marketIds', markets?.map((m) => ({ id: m.id, label: m.code })))}
          {form.audienceMode === 'SELECTED' && checks('Traders', 'userIds', people?.map((p) => ({ id: p.id, label: p.name })))}
          {form.audienceMode === 'GUESTS' && <p className="m-0 text-sm text-sub">Only people with your guest invite link can join. Floor members won’t see it.</p>}
          {checks('Co-hosts', 'coHostIds', people?.filter((p) => p.id !== (w?.host.id ?? me.data?.id)).map((p) => ({ id: p.id, label: p.name })))}
          {checks('Attachments from the library', 'libraryItemIds', library?.map((i) => ({ id: i.id, label: i.title })))}
        </Card>
        <ErrorText error={save.error} />
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" type="submit" disabled={save.isPending || form.title.trim().length < 3 || !form.startsAt}>{save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Schedule'}</Button>
          <Link to={editing ? `/webinars/${id}` : '/webinars'} className="self-center">Cancel</Link>
        </div>
      </form>
    </>
  );
}

// ───────────────────────────── detail ─────────────────────────────

interface Attendees {
  counts: { registered: number; joined: number; guests: number; guestsJoined: number; inRoom: number; capacity: number };
  registrations: { userId: string; name: string; registeredAt: string; joined: boolean }[];
  joined: { userId: string; name: string; connectedAt: string | null; leftAt: string | null }[];
  guests: { id: string; name: string; email: string; joined: boolean }[];
}

function GuestPanel({ w }: { w: WebinarView }) {
  const qc = useQueryClient();
  const [emails, setEmails] = useState('');
  const [copied, setCopied] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['webinar', w.id] });
  const invite = useMutation({
    mutationFn: () => api<{ sent: number }>(`/webinars/${w.id}/guest-invites`, { body: { emails: emails.split(/[\s,;]+/).filter(Boolean) } }),
    onSuccess: () => setEmails(''),
  });
  const rotate = useMutation({ mutationFn: () => api(`/webinars/${w.id}/guest-link/rotate`, { body: {} }), onSuccess: refresh });
  return (
    <Card>
      <CardHeader title="Guests" />
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-sm text-sub">Anyone with this link can register with their name and email and accept the disclaimer. It opens this webinar only, and stops working when it ends.</p>
        {w.guestLink && (
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-lg bg-raised px-3 py-2 text-xs" data-testid="guest-link">{w.guestLink}</code>
            <Button onClick={() => { void navigator.clipboard?.writeText(w.guestLink!); setCopied(true); }}>{copied ? 'Copied' : 'Copy'}</Button>
            <Button variant="ghost" onClick={() => rotate.mutate()}>New link</Button>
          </div>
        )}
        <Field label="Email the invite to (separate with commas)"><Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="name@example.com" /></Field>
        <div className="flex items-center gap-3"><Button onClick={() => invite.mutate()} disabled={!emails.trim() || invite.isPending}>Send invites</Button>{invite.data && <span className="text-sm text-up">Sent to {invite.data.sent}.</span>}</div>
        <ErrorText error={invite.error ?? rotate.error} />
      </div>
    </Card>
  );
}

function AttendeeReport({ id }: { id: string }) {
  const { data } = useQuery({ queryKey: ['webinar-attendees', id], queryFn: () => api<Attendees>(`/webinars/${id}/attendees`), refetchInterval: 20_000 });
  if (!data) return null;
  const c = data.counts;
  return (
    <Card>
      <CardHeader title="Attendees" />
      <div className="flex flex-col gap-3 p-4 text-sm">
        <span className="text-sub">{c.registered} registered · {c.joined} members joined · {c.guests} guests registered ({c.guestsJoined} joined) · {c.inRoom} in the room now (limit {c.capacity})</span>
        <div>
          <h3 className="m-0 mb-1 text-xs font-semibold text-muted uppercase">Members</h3>
          {!data.registrations.length && <span className="text-muted">No RSVPs yet.</span>}
          {data.registrations.map((r) => <div key={r.userId} className="flex justify-between gap-2 py-1"><span>{r.name}</span><span className="text-muted">{r.joined ? 'Joined' : 'Registered'}</span></div>)}
        </div>
        <div>
          <h3 className="m-0 mb-1 text-xs font-semibold text-muted uppercase">Guests</h3>
          {!data.guests.length && <span className="text-muted">No guests have registered.</span>}
          {data.guests.map((g) => <div key={g.id} className="flex flex-wrap justify-between gap-2 py-1"><span>{g.name} <span className="break-all text-muted">{g.email}</span></span><span className="text-muted">{g.joined ? 'Joined' : 'Registered'}</span></div>)}
        </div>
      </div>
    </Card>
  );
}

export function WebinarDetail() {
  const { id = '' } = useParams();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const now = useNow(30_000);
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: w, error } = useQuery({ queryKey: ['webinar', id], queryFn: () => api<WebinarView>(`/webinars/${id}`), refetchInterval: 30_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['webinar', id] });
    void qc.invalidateQueries({ queryKey: ['webinars'] });
  };
  const rsvp = useMutation({ mutationFn: (on: boolean) => api(`/webinars/${id}/rsvp`, on ? { body: {} } : { method: 'DELETE' }), onSuccess: refresh });
  const cal = useMutation({ mutationFn: () => downloadIcs(id) });
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const cancel = useMutation({ mutationFn: () => api(`/webinars/${id}/cancel`, { body: { reason: reason.trim() || null } }), onSuccess: () => { setCancelling(false); refresh(); } });
  const goLive = useMutation({ mutationFn: () => api(`/webinars/${id}/go-live`, { body: {} }), onSuccess: () => { refresh(); nav(`/webinars/${id}/live`); } });
  if (error) return <Card className="p-4"><p className="m-0">{errorText(error)} <Link to="/webinars">Back to webinars</Link></p></Card>;
  if (!w) return <Card className="p-4 text-sub">Loading…</Card>;
  const open = w.status === 'SCHEDULED' || w.status === 'LIVE';
  const start = new Date(w.startsAt).getTime();
  // broadcasts and recordings are run by Master Traders and Admins (INT-STR-02)
  const streamer = hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN');
  return (
    <>
      <PageHeader back={<Link to="/webinars">← Webinars</Link>} title={w.title} sub={<><StatusPill s={w.status} /> <span className="ml-1">{fmtDate(w.startsAt, tz)} · {fmtTime(w.startsAt, tz)}–{fmtTime(w.endsAt, tz)} ({w.durationMinutes} min)</span></>} />
      <Card className="flex flex-col gap-3 p-4">
        {w.status === 'SCHEDULED' && <p className="m-0 text-sm text-sub">Starts {fmtCountdown(w.startsAt, now)}</p>}
        {w.status === 'CANCELLED' && <p role="status" className="m-0 rounded-lg bg-down-bg px-3 py-2 text-down">Cancelled{w.cancelReason ? `: ${w.cancelReason}` : '.'}</p>}
        {w.description && <p className="m-0 leading-relaxed whitespace-pre-line text-ink2">{w.description}</p>}
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Host</dt><dd className="m-0">{w.host.name}</dd>
          {!!w.coHosts.length && <><dt className="text-muted">Co-hosts</dt><dd className="m-0">{w.coHosts.map((c) => c.name).join(', ')}</dd></>}
          <dt className="text-muted">For</dt><dd className="m-0">{w.audienceLabel}{w.markets.length ? `: ${w.markets.map((m) => m.code).join(', ')}` : ''}{w.selected.length ? `: ${w.selected.map((p) => p.name).join(', ')}` : ''}</dd>
          <dt className="text-muted">Registered</dt><dd className="m-0">{w.counts.registered}</dd>
        </dl>
        {(w.links?.youtube || w.links?.facebook) && (
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted">Watch again</span>
            {w.links.youtube && <a href={w.links.youtube} target="_blank" rel="noreferrer">On YouTube</a>}
            {w.links.facebook && <a href={w.links.facebook} target="_blank" rel="noreferrer">On Facebook</a>}
          </div>
        )}
        {!!w.attachments.length && (
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted">Materials</span>
            {w.attachments.map((a) => <Link key={a.id} to={`/library/items/${a.id}`}>{a.title}</Link>)}
          </div>
        )}
        {open && (
          <div className="flex flex-wrap gap-2">
            {!w.canRun && (w.registered ? <Button onClick={() => rsvp.mutate(false)} disabled={rsvp.isPending}>✓ Registered · cancel</Button> : <Button variant="primary" onClick={() => rsvp.mutate(true)} disabled={rsvp.isPending}>RSVP</Button>)}
            <Button onClick={() => cal.mutate()}>Add to calendar (.ics)</Button>
            {(w.canJoin || w.status === 'LIVE') && <Link to={`/webinars/${id}/live`} className="rounded-lg bg-up px-4 py-2.5 font-bold text-bg no-underline hover:text-bg">{w.canRun ? (w.status === 'LIVE' ? 'Open the live view' : 'Open backstage') : 'Join live'}</Link>}
            {w.canRun && w.status === 'SCHEDULED' && start - now > 30 * 60_000 && <span className="self-center text-sm text-muted">Backstage opens 30 minutes before the start.</span>}
          </div>
        )}
        {w.canRun && w.status === 'SCHEDULED' && <div><Button variant="success" onClick={() => goLive.mutate()} disabled={goLive.isPending}>Go live now</Button></div>}
        <ErrorText error={rsvp.error ?? cal.error ?? goLive.error} />
      </Card>
      {w.canEdit && (
        <div className="flex flex-wrap gap-3">
          <Link to={`/webinars/${id}/edit`} className="rounded-lg border border-line2 px-4 py-2 font-semibold text-ink no-underline">Edit</Link>
          <Button variant="danger" onClick={() => setCancelling((v) => !v)}>Cancel webinar</Button>
        </div>
      )}
      {cancelling && (
        <Card className="flex flex-col gap-3 p-4">
          <Field label="Reason (sent to people who registered)"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Field>
          <ErrorText error={cancel.error} />
          <div><Button variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>Yes, cancel it</Button></div>
        </Card>
      )}
      {w.canManage && streamer && w.status !== 'CANCELLED' && <BroadcastPanel webinarId={id} title={w.title} description={w.description} />}
      {w.canManage && streamer && w.status === 'ENDED' && <RecordingsCard webinarId={id} />}
      {w.canManage && open && <GuestPanel w={w} />}
      {w.canManage && <AttendeeReport id={id} />}
    </>
  );
}

// ───────────────────────────── live / backstage ─────────────────────────────

export function WebinarLive() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: w, error } = useQuery({ queryKey: ['webinar', id], queryFn: () => api<WebinarView>(`/webinars/${id}`), refetchInterval: 15_000 });
  const me = useMe();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['webinar', id] });
    void qc.invalidateQueries({ queryKey: ['broadcast', id] });
    void qc.invalidateQueries({ queryKey: ['recordings', id] });
  };
  const act = useMutation({ mutationFn: (p: { path: string; body?: object }) => api(`/webinars/${id}/${p.path}`, { body: p.body ?? {} }), onSuccess: () => { refresh(); void qc.invalidateQueries({ queryKey: ['webinars'] }); } });
  const live = useMemo(() => memberLiveApi(id), [id]);
  if (error) return <Card className="p-4"><p className="m-0">{errorText(error)} <Link to="/webinars">Back to webinars</Link></p></Card>;
  if (!w) return <Card className="p-4 text-sub">Loading…</Card>;
  const hostView = w.canRun;
  return (
    <>
      <PageHeader back={<Link to={`/webinars/${id}`}>← Details</Link>} title={w.title} sub={<><StatusPill s={w.status} /> {hostView ? (w.status === 'LIVE' ? 'You’re live' : 'Backstage') : `Hosted by ${w.host.name}`}</>} />
      {!w.livekit.configured && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">Live video isn’t set up yet. Chat and Q&A work, but there is no video until LiveKit is connected.</p>}
      {hostView && (
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            {w.status === 'SCHEDULED' && <Button variant="success" onClick={() => act.mutate({ path: 'go-live' })} disabled={act.isPending}>Go live</Button>}
            {w.status === 'LIVE' && <Button variant="danger" onClick={() => window.confirm('End the webinar for everyone?') && act.mutate({ path: 'end' })} disabled={act.isPending}>End webinar</Button>}
            <span className="text-sm text-sub">{w.capacity.inRoom} of {w.capacity.max} viewer places in use</span>
          </div>
          <Segmented label="Layout" options={WEBINAR_LAYOUTS.map((l) => ({ value: l, label: WEBINAR_LAYOUT_LABEL[l] }))} value={w.layout} onChange={(layout) => act.mutate({ path: 'layout', body: { layout } })} />
          <ErrorText error={act.error} />
        </Card>
      )}
      {hostView && hasRole(me.data?.roles, 'MASTER_TRADER', 'ADMIN') && w.status !== 'CANCELLED' && (
        <div className="grid gap-3.5 lg:grid-cols-2">
          <BroadcastPanel webinarId={id} title={w.title} description={w.description} />
          <RecordingsCard webinarId={id} live={w.status === 'LIVE'} />
        </div>
      )}
      <WebinarRoom id={id} live={live} layout={w.layout} host={hostView} canJoinNow={w.canJoin} joinHint={w.status === 'SCHEDULED' ? 'Not live yet. You’ll get an alert the moment it starts.' : 'This webinar is over.'} joinLabel={hostView ? 'Join as host' : 'Join the webinar'} onChange={refresh} />
    </>
  );
}
